/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import { useMemo } from 'react';

import { type PolymorphicCommonProps } from '../_common/types';
import {
  getKbdKeysLabelMap,
  getKbdKeysMap,
  type KbdKey,
  type OperatingSystem,
} from './kbd-utils';

type KbdProps = {
  /**
   * The modifier keys to display in the keyboard shortcut.
   */
  modifierKeys?: KbdKey[];
  /**
   * The main keys to display in the keyboard shortcut.
   */
  keys?: React.ReactNode[];
  /**
   * Override the operating system detection for key symbols
   */
  os?: OperatingSystem;
};

export const Kbd = <T extends React.ElementType = 'span'>({
  modifierKeys,
  keys,
  os,
  as,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, KbdProps>) => {
  const Component: React.ElementType = as ?? 'span';

  const modifierKeysContent = useMemo(() => {
    if (modifierKeys === undefined) {
      return null;
    }

    const keyMap = getKbdKeysMap(os);
    const labelMap = getKbdKeysLabelMap(os);

    return modifierKeys?.map((key) => (
      <abbr className="ndl-kbd-key" key={key} title={labelMap[key]}>
        {keyMap[key]}
      </abbr>
    ));
  }, [modifierKeys, os]);

  const keysContent = useMemo(() => {
    if (keys === undefined) {
      return null;
    }

    return keys?.map((key, index) => {
      if (index === 0) {
        return (
          <span className="ndl-kbd-key" key={key?.toString()}>
            {key}
          </span>
        );
      }
      return (
        <>
          <span className="ndl-kbd-then">Then</span>
          <span className="ndl-kbd-key" key={key?.toString()}>
            {key}
          </span>
        </>
      );
    });
  }, [keys]);

  const classes = classNames('ndl-kbd', className);
  return (
    <Component
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {modifierKeysContent}
      {keysContent}
    </Component>
  );
};
